import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  F0FormSubmitResult,
  F0WizardForm,
  F0WizardFormPerSectionSubmitArg,
} from '@factorialco/f0-react'
import { AssignBudgetToTrainingClassDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { i18nDateToString } from 'modules/core/lib/dates'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { track } from 'modules/core/lib/Tracking'

import { useCreateBudget } from 'modules/finance/hooks/useBudgets'
import { events } from 'modules/finance/lib/events'

import { TrainingsBudgetSchema, useTrainingsBudgetForm } from './form'

interface Props {
  legalEntities: Array<{ id: string; currency: string; main: boolean }>
  onSuccess: (budgetId: string) => void
  onError: () => void
}

export const TrainingsBudgetWizard = ({ legalEntities, onSuccess, onError }: Props) => {
  const session = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { createBudget } = useCreateBudget()

  const currencyMap = useMemo(
    () => Object.fromEntries(legalEntities.map((le) => [le.id, le.currency])),
    [legalEntities],
  )

  const mainLegalEntity = useMemo(
    () => legalEntities.find((le) => le.main) ?? legalEntities[0],
    [legalEntities],
  )
  const [assignBudgetToTrainingClass] = useMutation(AssignBudgetToTrainingClassDocument, {
    extractorFn: (data) => data.trainingsMutations.updateTrainingsTrainingClass,
  })

  const handleSubmit = useCallback(
    async (
      arg: F0WizardFormPerSectionSubmitArg<TrainingsBudgetSchema>,
    ): Promise<F0FormSubmitResult> => {
      if (arg.sectionId !== 'allocation') {
        return { success: true }
      }

      const { legalEntityId, name, description } = arg.fullData.basicDetails
      const { amountCents, effectiveFrom, effectiveUntil } = arg.fullData.amountsAndDates
      const { allocationField } = arg.fullData.allocation
      const { selectedTrainingClassIds } = allocationField

      if (!legalEntityId) {
        return { success: false, errors: {} }
      }

      const effectiveFromStr = i18nDateToString(effectiveFrom, 'backend_datetime')
      const effectiveUntilStr = effectiveUntil
        ? i18nDateToString(effectiveUntil, 'backend_datetime')
        : undefined

      const response = await createBudget({
        name,
        description,
        legalEntityId,
        amountCents: toCents(amountCents),
        effectiveFrom: effectiveFromStr,
        effectiveUntil: effectiveUntilStr,
        periodicity: 'none',
        ownerEmployeeIds: [session.employee.id],
        viewerEmployeeIds: [],
        spenderEmployeeIds: [],
        budgetableBy: 'training_class',
      })

      const result = handleResult(response, onError)

      if (!result.success) {
        onError()
        return { success: false, errors: {} }
      }

      const budgetId = result.data.budget?.id

      track(events.completeAddBudget, { budgetId, type: 'trainings' })

      // Assign selected training classes to the new budget
      if (budgetId && selectedTrainingClassIds.length > 0) {
        await Promise.all(
          selectedTrainingClassIds.map((classId) =>
            assignBudgetToTrainingClass({
              variables: {
                id: classId,
                budgetId,
              },
            })
          )
        )
      }

      if (budgetId) {
        onSuccess(budgetId)
      } else {
        onError()
      }

      return { success: true }
    },
    [createBudget, assignBudgetToTrainingClass, session.employee.id, onSuccess, onError],
  )

  const { formDefinition, steps } = useTrainingsBudgetForm({
    defaultLegalEntityId: mainLegalEntity?.id,
    currency: mainLegalEntity?.currency ?? session.company.currency ?? '',
    currencyMap,
    onSubmit: handleSubmit,
  })

  return (
    <F0WizardForm
      isOpen
      title={i18n.t('budgets.forms.trainings_budget.title')}
      formDefinition={formDefinition}
      steps={steps}
      onClose={() => navigate(navigation.expenses.budgets.fullPath())}
    />
  )
}
